/* eslint-disable */

import React, { Fragment, MouseEvent, useEffect, useState } from 'react';
import { EpIcon } from '../EpIcon';

import './EpButtonCircle.scss';
import Image from 'next/image';

interface EpButtonCircleProps {
  buttonId: string;
  buttonType: 'primary' | 'secondary' | 'secondary-outline';
  buttonSize: 'sm' | 'md' | 'lg';
  buttonLeftText?: string;
  buttonRightText?: string;
  buttonIconType: 'icon' | 'img';
  buttonIconName?: string;
  buttonIconImgSrc?: string;
  buttonIconImgAlt?: string;
  buttonIsDisabled?: boolean;
  buttonIsActive?: boolean;
  customClasses?: string;
  buttonFormId?: string;
  buttonActionType?: 'submit' | 'reset' | 'button';
  buttonDataBsToggle?: string;
  buttonAriaExpanded?: boolean;
  buttonDataBsAutoClose?: boolean;
  buttonDataBsTarget?: string;
  buttonAriaControls?: string;
  buttonDataBsDismiss?: string;
  buttonAriaLabel?: string;
  buttonOnClickFunc?: (args?: unknown) => void;
  buttonLinkHref?: string;
  buttonLinkTarget?: string;
  buttonLinkHasDownloadEnabled?: boolean;
  buttonIconSizeCustom?: number;
}

export const EpButtonCircle: React.FC<EpButtonCircleProps> = ({
  buttonId,
  buttonType,
  buttonSize,
  buttonLeftText,
  buttonRightText,
  buttonIconType,
  buttonIconName,
  buttonIconImgSrc = '',
  buttonIconImgAlt = '',
  buttonIsDisabled = false,
  buttonIsActive = false,
  customClasses = '',
  buttonFormId,
  buttonActionType = 'button',
  buttonDataBsToggle = '',
  buttonAriaExpanded = false,
  buttonDataBsAutoClose = false,
  buttonDataBsTarget = '',
  buttonAriaControls = '',
  buttonDataBsDismiss = '',
  buttonAriaLabel = '',
  buttonOnClickFunc = null,
  buttonLinkHref = '',
  buttonLinkTarget = '_blank',
  buttonLinkHasDownloadEnabled = false,
  buttonIconSizeCustom = 0,
}) => {
  const [buttonIsDisabledState, setButtonIsDisabledState] = useState(false);
  const buttonDisabledClass = 'ep-button-circle--disabled';
  const [buttonIconSize, setButtonIconSize] = useState(buttonIconSizeCustom);

  useEffect(() => {
    if (buttonIconSizeCustom) return;

    switch (buttonSize) {
      case 'sm':
        setButtonIconSize(12);
        break;
      case 'md':
        setButtonIconSize(16);
        break;
      case 'lg':
        setButtonIconSize(20);
        break;
      default:
        setButtonIconSize(0);
        break;
    }

    setButtonIsDisabledState(buttonIsDisabled);
  }, [buttonSize, buttonIsDisabled]);

  const handleButtonClick = (event: MouseEvent) => {
    if (!buttonLinkHref && buttonOnClickFunc) {
      event.preventDefault();
      buttonOnClickFunc(event);
    }
  };

  const getButtonContentStruture = () => {
    if (buttonIconType === 'icon' && buttonIconName) {
      return (
        <div className="ep-button-circle__icon">
          <EpIcon iconName={buttonIconName} iconSize={buttonIconSize} />
        </div>
      );
    }

    return (
      <div className="ep-button-circle__icon">
        <Image
          src={buttonIconImgSrc}
          alt={buttonIconImgAlt}
          height={buttonIconSize}
          width={buttonIconSize}
        />
      </div>
    );
  };

  return (
    <Fragment>
      {buttonLinkHref ? (
        <a
          id={buttonId}
          className={`btn ep-button-circle ep-button-circle--${buttonType} ep-button-circle--${buttonSize} ${
            buttonIsActive ? 'ep-button-circle--active' : ''
          } ${buttonIsDisabledState ? buttonDisabledClass : ''} ${
            customClasses ? customClasses : ''
          }`}
          {...(buttonLinkHref && { href: buttonLinkHref })}
          {...(buttonLinkHref && { target: buttonLinkTarget })}
          {...(buttonLinkHref && { rel: 'noopener noreferrer' })}
          {...(buttonLinkHasDownloadEnabled && { download: '' })}
        >
          {buttonLeftText && (
            <span className="ep-button-circle__text">{buttonLeftText}</span>
          )}
          {getButtonContentStruture()}
          {buttonRightText && (
            <span className="ep-button-circle__text">{buttonRightText}</span>
          )}
        </a>
      ) : (
        <button
          id={buttonId}
          type={buttonActionType}
          className={`btn ep-button-circle ep-button-circle--${buttonType} ep-button-circle--${buttonSize} ${
            buttonIsActive ? 'ep-button-circle--active' : ''
          } ${buttonIsDisabledState ? buttonDisabledClass : ''} ${
            customClasses ? customClasses : ''
          }`}
          {...(buttonFormId && { form: buttonFormId })}
          {...(buttonIsDisabledState && { disabled: buttonIsDisabledState })}
          {...(buttonDataBsToggle && { 'data-bs-toggle': buttonDataBsToggle })}
          {...(buttonAriaExpanded && { 'aria-expanded': buttonAriaExpanded })}
          {...(buttonDataBsAutoClose && {
            'data-bs-auto-close': buttonDataBsAutoClose,
          })}
          {...(buttonDataBsTarget && { 'data-bs-target': buttonDataBsTarget })}
          {...(buttonDataBsDismiss && {
            'data-bs-dismiss': buttonDataBsDismiss,
          })}
          {...(buttonAriaControls && { 'aria-controls': buttonAriaControls })}
          {...(buttonAriaLabel && { 'aria-label': buttonAriaLabel })}
          {...(buttonOnClickFunc && { onClick: handleButtonClick })}
        >
          {buttonLeftText && (
            <span className="ep-button-circle__text">{buttonLeftText}</span>
          )}
          {getButtonContentStruture()}
          {buttonRightText && (
            <span className="ep-button-circle__text">{buttonRightText}</span>
          )}
        </button>
      )}
    </Fragment>
  );
};
